import Phaser from 'phaser';
import { apiBaseUrl } from '../../platform/index.js';
import { getAuth, signOut } from '../../store/auth.js';
import { Button } from './Button.js';
import { TextInputOverlay } from './TextInputOverlay.js';
import { COLORS, FONT_HEADER, FONT_MONO } from '../style.js';
import { DESIGN_HEIGHT, DESIGN_WIDTH } from '../PhaserGame.js';

// Visual constants.
const BACKDROP_ALPHA = 0.78;
const BACKDROP_DEPTH = 1000;
const PANEL_DEPTH = 1001;
const PANEL_WIDTH = 620;
const PANEL_RADIUS = 12;

const CONFIRM_KEYWORD = 'DELETE';

/**
 * Modal lifecycle states. Drives which body content renders.
 *
 *   loading  → fetching active-game count from GET /api/account
 *   ready    → user can type DELETE and submit
 *   working  → DELETE request in flight, signing out, etc.
 *   error    → something failed; user can retry or cancel
 */
type State = 'loading' | 'ready' | 'working' | 'error';

export interface DeleteAccountModalOptions {
  /** Called when the user explicitly cancels (Cancel button only). */
  readonly onCancel: () => void;
  /**
   * Called after the account is deleted AND the user is signed out.
   * SettingsScene routes to Home (or wherever) in response.
   */
  readonly onSuccess: () => void;
}

interface AccountStatus {
  readonly activeGameCount: number;
  readonly username: string | null;
}

/**
 * Two-step destructive confirmation. Requires the user to type
 * `DELETE` to enable the red destructive button, then runs:
 *
 *   1. GET /api/account → status preview (count of active games)
 *   2. DELETE /api/account → forfeit + cascade-delete
 *   3. signOut() → clear local session
 *   4. onSuccess() → caller routes away
 *
 * Backdrop tap is INTENTIONALLY not a cancel. Destructive flows
 * should require an explicit Cancel tap so an accidental
 * background-tap doesn't dismiss the confirmation.
 *
 * Lifecycle: caller `new DeleteAccountModal(scene, opts)` to show;
 * the modal calls `opts.onCancel` or `opts.onSuccess` and then
 * destroys itself. SettingsScene must re-create the modal on next
 * tap (it's single-use).
 */
export class DeleteAccountModal {
  private readonly scene: Phaser.Scene;
  private readonly opts: DeleteAccountModalOptions;

  // Backdrop + panel rendered once; body re-rendered on state changes.
  private readonly objects: Phaser.GameObjects.GameObject[] = [];
  // Body objects (everything inside the panel that changes per state).
  private bodyObjects: Phaser.GameObjects.GameObject[] = [];
  private buttons: Button[] = [];
  private input: TextInputOverlay | null = null;

  // Cached status from GET /api/account.
  private status: AccountStatus | null = null;
  // Tracks the typed value so the confirm button can enable/disable.
  private typedValue = '';
  // Last error message, when state === 'error'.
  private errorMsg = '';
  // Current state. Drives renderBody().
  private currentState: State = 'loading';

  // Guards against double-cleanup on shutdown.
  private destroyed = false;

  constructor(scene: Phaser.Scene, opts: DeleteAccountModalOptions) {
    this.scene = scene;
    this.opts = opts;

    this.drawBackdrop();
    this.drawPanel();
    this.renderBody();

    // Clean up if the scene shuts down (e.g. user hits browser back
    // before they finish the flow).
    this.scene.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.destroy();
    });

    // Kick off the status fetch.
    void this.fetchStatus();
  }

  // --- Chrome ---

  private drawBackdrop(): void {
    const bg = this.scene.add
      .rectangle(
        DESIGN_WIDTH / 2,
        DESIGN_HEIGHT / 2,
        DESIGN_WIDTH,
        DESIGN_HEIGHT,
        0x000000,
        BACKDROP_ALPHA
      )
      .setDepth(BACKDROP_DEPTH)
      // Mark interactive so taps don't fall through to underlying
      // scene controls. NO pointerup → cancel; backdrop tap is a
      // no-op for destructive flows.
      .setInteractive();
    this.objects.push(bg);
  }

  private drawPanel(): void {
    // Tall centered card. Phaser's rectangle GameObject doesn't
    // support corner radius natively, so draw the panel as a
    // rounded-rect via Graphics — fill + stroke in one object,
    // panel-depth above the backdrop.
    const panelHeight = 720;
    const left = DESIGN_WIDTH / 2 - PANEL_WIDTH / 2;
    const top = DESIGN_HEIGHT / 2 - panelHeight / 2;

    const panel = this.scene.add.graphics().setDepth(PANEL_DEPTH);
    panel.fillStyle(COLORS.bgDeepInt, 1);
    panel.fillRoundedRect(left, top, PANEL_WIDTH, panelHeight, PANEL_RADIUS);
    panel.lineStyle(2, this.destructiveColorInt(), 0.95);
    panel.strokeRoundedRect(left, top, PANEL_WIDTH, panelHeight, PANEL_RADIUS);
    this.objects.push(panel);
  }

  // --- State-driven body ---

  private renderBody(): void {
    // Tear down any previous body objects + input + buttons before
    // redrawing. Cheaper than diffing for a screen this simple.
    for (const o of this.bodyObjects) o.destroy();
    this.bodyObjects = [];
    this.input?.destroy();
    this.input = null;
    for (const b of this.buttons) b.destroy();
    this.buttons = [];

    switch (this.currentState) {
      case 'loading':
        this.renderLoading();
        break;
      case 'ready':
        this.renderReady();
        break;
      case 'working':
        this.renderWorking();
        break;
      case 'error':
        this.renderError();
        break;
    }
  }

  private renderLoading(): void {
    this.addTitle('Delete account');
    this.addBody(
      'Checking your active games…',
      DESIGN_HEIGHT / 2 - 40,
      COLORS.textMuted
    );
    this.addCancelButton(DESIGN_HEIGHT / 2 + 240);
  }

  private renderReady(): void {
    const cx = DESIGN_WIDTH / 2;
    const top = DESIGN_HEIGHT / 2 - 280;

    this.addTitle('Delete account', top + 20);

    const greeting =
      this.status?.username != null
        ? `Goodbye, ${this.status.username}.`
        : 'Goodbye.';
    this.addBody(greeting, top + 100, this.destructiveColorHex());

    // Body copy varies by active-game count. Team-aware reassurance
    // is part of the copy contract from the design discussion: lets
    // players know their teammates won't be left high and dry.
    const count = this.status?.activeGameCount ?? 0;
    const lines = [
      'This will permanently delete your account.',
      '',
      count > 0
        ? `Your ${count} active game${count === 1 ? '' : 's'} will be forfeited. Tiles you control will be handed to your teammates where possible.`
        : 'You have no active games to forfeit.',
      '',
      'This action cannot be undone.',
    ];
    this.addBodyMulti(lines.join('\n'), top + 160);

    // Confirm input — the "type DELETE to enable the destructive
    // button" gate. iOS auto-zoom is avoided by the 16px font-size
    // floor inside TextInputOverlay.
    this.addInputLabel(top + 410);
    this.input = new TextInputOverlay({
      scene: this.scene,
      x: cx,
      y: top + 460,
      width: 360,
      height: 64,
      type: 'text',
      placeholder: CONFIRM_KEYWORD,
      maxLength: 12,
      fontSizeDesign: 26,
      onChange: (v) => {
        this.typedValue = v;
        // Re-render so the confirm button enabled-state updates.
        // Cheap: just one button to toggle.
        this.refreshConfirmEnabled();
      },
      onSubmit: () => {
        if (this.canConfirm()) void this.handleDelete();
      },
    });

    // Buttons row: Cancel (left) + Delete account (right, destructive).
    this.addCancelButton(top + 600, cx - 130);
    this.addConfirmButton(top + 600, cx + 130);
    this.refreshConfirmEnabled();
  }

  private renderWorking(): void {
    this.addTitle('Deleting account…');
    this.addBody(
      'Forfeiting your active games, then removing your account.',
      DESIGN_HEIGHT / 2 - 40,
      COLORS.textMuted
    );
    // No buttons — this state is uncancellable. Either it completes
    // → onSuccess; or it fails → switches to 'error'.
  }

  private renderError(): void {
    const top = DESIGN_HEIGHT / 2 - 240;
    this.addTitle('Couldn\'t delete', top + 20);
    this.addBodyMulti(
      `${this.errorMsg}\n\nNothing changed. You can try again or cancel.`,
      top + 100
    );
    this.addCancelButton(top + 480, DESIGN_WIDTH / 2 - 130);
    // "Try again" goes back to ready state — preserves the typed
    // value so the user doesn't have to retype DELETE.
    this.buttons.push(
      new Button(this.scene, {
        x: DESIGN_WIDTH / 2 + 130,
        y: top + 480,
        width: 200,
        height: 64,
        text: 'Try again',
        style: 'primary',
        onTap: () => {
          if (this.status) {
            this.currentState = 'ready';
          } else {
            this.currentState = 'loading';
            void this.fetchStatus();
          }
          this.renderBody();
        },
      })
    );
    this.buttons[this.buttons.length - 1]!.setDepth(PANEL_DEPTH + 2);
  }

  // --- Body helpers ---

  private addTitle(text: string, y: number = DESIGN_HEIGHT / 2 - 280): void {
    const t = this.scene.add
      .text(DESIGN_WIDTH / 2, y, text, {
        fontFamily: FONT_HEADER,
        fontSize: '32px',
        color: this.destructiveColorHex(),
        fontStyle: '700',
      })
      .setLetterSpacing(2)
      .setOrigin(0.5)
      .setDepth(PANEL_DEPTH + 1);
    this.bodyObjects.push(t);
  }

  private addBody(text: string, y: number, color: string): void {
    const t = this.scene.add
      .text(DESIGN_WIDTH / 2, y, text, {
        fontFamily: FONT_MONO,
        fontSize: '16px',
        color,
        align: 'center',
        wordWrap: { width: PANEL_WIDTH - 80 },
      })
      .setOrigin(0.5)
      .setDepth(PANEL_DEPTH + 1);
    this.bodyObjects.push(t);
  }

  private addBodyMulti(
    text: string,
    y: number,
    color: string = COLORS.textPrimary
  ): void {
    const t = this.scene.add
      .text(DESIGN_WIDTH / 2, y, text, {
        fontFamily: FONT_MONO,
        fontSize: '15px',
        color,
        align: 'center',
        wordWrap: { width: PANEL_WIDTH - 80 },
      })
      .setLineSpacing(4)
      .setOrigin(0.5, 0)
      .setDepth(PANEL_DEPTH + 1);
    this.bodyObjects.push(t);
  }

  private addInputLabel(y: number): void {
    const t = this.scene.add
      .text(DESIGN_WIDTH / 2, y, `TYPE "${CONFIRM_KEYWORD}" TO CONFIRM`, {
        fontFamily: FONT_MONO,
        fontSize: '12px',
        color: COLORS.textSecondary,
        fontStyle: '700',
      })
      .setLetterSpacing(3)
      .setOrigin(0.5)
      .setDepth(PANEL_DEPTH + 1);
    this.bodyObjects.push(t);
  }

  private addCancelButton(y: number, x: number = DESIGN_WIDTH / 2): void {
    const btn = new Button(this.scene, {
      x,
      y,
      width: 200,
      height: 64,
      text: 'Cancel',
      onTap: () => {
        if (this.currentState === 'working') return;
        this.handleCancel();
      },
    });
    btn.setDepth(PANEL_DEPTH + 2);
    this.buttons.push(btn);
  }

  private addConfirmButton(y: number, x: number): void {
    // The destructive button. Style stays 'primary' but we'd
    // ideally tint it red — the Button view doesn't expose a
    // destructive variant yet, so we lean on the disabled state
    // to gate the action and let the typed-DELETE pattern carry
    // the destructive intent for now.
    const btn = new Button(this.scene, {
      x,
      y,
      width: 200,
      height: 64,
      text: 'Delete forever',
      style: 'primary',
      onTap: () => {
        if (!this.canConfirm()) return;
        void this.handleDelete();
      },
    });
    btn.setDepth(PANEL_DEPTH + 2);
    this.buttons.push(btn);
  }

  // --- Logic ---

  private refreshConfirmEnabled(): void {
    // Confirm is always the LAST button added in the ready state.
    // Could also keep a named ref; this is fine for now.
    const confirm = this.buttons[this.buttons.length - 1];
    if (!confirm) return;
    confirm.setEnabled(this.canConfirm());
  }

  private canConfirm(): boolean {
    return (
      this.currentState === 'ready' && this.typedValue.trim() === CONFIRM_KEYWORD
    );
  }

  private async fetchStatus(): Promise<void> {
    const session = getAuth().session;
    if (!session) {
      this.showError('You are not signed in.');
      return;
    }
    try {
      const res = await fetch(`${apiBaseUrl()}/api/account`, {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      if (!res.ok) {
        this.showError(`Couldn't load your account (HTTP ${res.status}).`);
        return;
      }
      const data = (await res.json()) as AccountStatus;
      this.status = data;
      if (this.destroyed) return;
      this.currentState = 'ready';
      this.renderBody();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      this.showError(`Network error: ${msg}`);
    }
  }

  private async handleDelete(): Promise<void> {
    if (!this.canConfirm()) return;
    this.currentState = 'working';
    this.renderBody();

    const session = getAuth().session;
    if (!session) {
      this.showError('You are not signed in.');
      return;
    }
    try {
      const res = await fetch(`${apiBaseUrl()}/api/account`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        this.showError(
          body.error
            ? `Delete failed: ${body.error}.`
            : `Delete failed (HTTP ${res.status}).`
        );
        return;
      }
      // 204 → account is gone. Clear local session and signal success.
      await signOut();
      if (this.destroyed) return;
      this.opts.onSuccess();
      this.destroy();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      this.showError(`Network error: ${msg}`);
    }
  }

  private handleCancel(): void {
    this.opts.onCancel();
    this.destroy();
  }

  private showError(msg: string): void {
    this.errorMsg = msg;
    this.currentState = 'error';
    this.renderBody();
  }

  // --- Cleanup ---

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    for (const o of this.objects) o.destroy();
    this.objects.length = 0;
    for (const o of this.bodyObjects) o.destroy();
    this.bodyObjects = [];
    this.input?.destroy();
    this.input = null;
    for (const b of this.buttons) b.destroy();
    this.buttons = [];
  }

  // --- Color helpers ---

  /**
   * Destructive accent color. The whole modal is destructive in
   * intent — title, panel stroke, and destructive button all share
   * this red so the visual chord reads as a single warning.
   */
  private destructiveColorInt(): number {
    return COLORS.accentRedInt;
  }
  private destructiveColorHex(): string {
    return COLORS.accentRedHex;
  }
}
